import { moeda } from '../../util/util';
import { ROTULO_ETAPA_VIGENTE } from './colunaVigentePolicy';
import { textoOrigem } from '../../components/moedaOrigemPolicy';

// Valor do fornecedor na tela de envio/Efetivo = o MESMO teto da trava do servidor
// (OrcamentoPagamentoController::valoresVigentesPorFornecedor, última coluna preenchida no
// agregado do fornecedor; ledger exterior concluído = principal real). `total_orcamento_brl`
// está em R$, a mesma unidade das parcelas; `total_orcamento_moeda` na moeda do orçamento.
// Resposta antiga (sem os campos) cai na hierarquia por somas × câmbio.
const numero = (valor) => {
    if (valor === null || valor === undefined || valor === '') return null;
    const convertido = Number(valor);
    return Number.isFinite(convertido) ? convertido : null;
};

export function valorLegadoFornecedor(forn = {}) {
    if (forn.total_fechamento > 0) return { valor: forn.total_fechamento, origem: 'Fechamento' };
    if (forn.total_negociado > 0) return { valor: forn.total_negociado, origem: 'Negociado' };
    if (forn.total_preevento > 0) return { valor: forn.total_preevento, origem: 'Pré-Evento' };
    return { valor: 0, origem: '' };
}

export function valorVigenteFornecedor(forn = {}, cambio = 1) {
    const fator = Number.isFinite(cambio) && cambio > 0 ? cambio : 1;
    const brl = numero(forn.total_orcamento_brl);
    if (brl !== null) {
        const original = numero(forn.total_orcamento_moeda) ?? numero(forn.total_orcamento);
        return { valorBrl: brl, valorOrig: original ?? brl / fator, origem: ROTULO_ETAPA_VIGENTE[forn.etapa_vigente] || '', daApi: true };
    }
    const legado = valorLegadoFornecedor(forn);
    return { valorBrl: legado.valor * fator, valorOrig: legado.valor, origem: legado.origem, daApi: false };
}

// "Orçamento do fornecedor · já parcelado · disponível" em R$, como o servidor conta.
export function saldoFornecedorApi(forn = {}) {
    const total = numero(forn.total_orcamento_brl);
    const saldo = numero(forn.saldo_orcamento_brl);
    if (total === null || saldo === null) return null;
    // encargos = IOF/IR/taxa da cotação do banco (16/10): estão nas parcelas, não no orçamento; o
    // saldo da API já os soma (igual à trava) e a tela mostra a linha para a conta fechar.
    return { orcamento: total, parcelado: Number(forn.total_parcelado || 0), saldo, encargos: numero(forn.encargos_cambio_brl) || 0, daApi: true };
}

// SALDO do cabeçalho do card (Previsão): valor do fornecedor + encargos do câmbio do banco − parcelado.
// Sem os encargos, fornecedor com câmbio aprovado ficava com saldo negativo (ex.: -190,00).
export function saldoCabecalhoFornecedor(valorBrl, totalParcelado, forn = {}) {
    return Math.round((Number(valorBrl || 0) + (numero(forn.encargos_cambio_brl) || 0) - Number(totalParcelado || 0)) * 100) / 100;
}

const SIMBOLO_MOEDA_FORN = { real: 'R$', dolar: 'US$', euro: '€', iene: '¥', libra: '£', dolarcad: 'C$' };
export const formatarCambioEF = (valor) => Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

// Moeda do FORNECEDOR (27/09): cada fornecedor na moeda dele (API: moeda_fornecedor, cambio_fornecedor,
// total_orcamento_moeda_fornecedor), com o câmbio e o R$ (teto da trava). Sem os campos (API antiga) ou
// fornecedor 'misto' (linhas em moedas diferentes), cai na moeda do orçamento.
export function valorFornecedorNaMoedaDele(forn = {}, moedaOrcamento = 'real', cambioOrcamento = 1) {
    const vigente = valorVigenteFornecedor(forn, cambioOrcamento);
    const moedaForn = String(forn.moeda_fornecedor || '').toLowerCase();
    if (moedaForn && moedaForn !== 'misto' && SIMBOLO_MOEDA_FORN[moedaForn] && forn.total_orcamento_moeda_fornecedor != null) {
        return { simbolo: SIMBOLO_MOEDA_FORN[moedaForn], moeda: moedaForn, original: Number(forn.total_orcamento_moeda_fornecedor),
            cambio: forn.cambio_fornecedor != null ? Number(forn.cambio_fornecedor) : null, brl: vigente.valorBrl, origem: vigente.origem,
            estrangeira: moedaForn !== 'real', proprio: !!forn.cambio_fornecedor_proprio,
            // Fechamento conciliado: invoice paga + câmbio médio real do ledger (igual às telas financeiras).
            medio: !!forn.cambio_fornecedor_medio };
    }
    // Misto com a quebra da API (09/10): "US$ 1.000,00 + € 200,00 = R$ 6.720,00". Sem câmbio único —
    // parcela não é convertida (textoParcelaNaMoedaFornecedor devolve null).
    const quebra = Array.isArray(forn.por_moeda_fornecedor) ? forn.por_moeda_fornecedor.filter((q) => q && SIMBOLO_MOEDA_FORN[q.moeda]) : [];
    if (moedaForn === 'misto' && quebra.length > 0) {
        return { simbolo: null, moeda: 'misto', original: null, cambio: null, brl: vigente.valorBrl, origem: vigente.origem,
            estrangeira: quebra.some((q) => q.moeda !== 'real'), proprio: false, misto: true, porMoeda: quebra };
    }
    const moedaOrc = String(moedaOrcamento || 'real').toLowerCase();
    return { simbolo: SIMBOLO_MOEDA_FORN[moedaOrc] || 'R$', moeda: moedaOrc, original: vigente.valorOrig,
        cambio: moedaOrc === 'real' ? 1 : cambioOrcamento, brl: vigente.valorBrl, origem: vigente.origem,
        estrangeira: moedaOrc !== 'real', proprio: false, misto: moedaForn === 'misto' };
}

// "€ 950,00 × 5,9000 → R$ 5.605,00" (estrangeira) ou "R$ 1.000,00" (BRL).
export function textoValorFornecedor(v = {}) {
    if (v.misto && Array.isArray(v.porMoeda) && v.porMoeda.length > 0) return textoQuebraPorMoeda(v.porMoeda, v.brl);
    if (!v.estrangeira) return 'R$ ' + moeda(v.brl);
    return v.simbolo + ' ' + moeda(v.original) + (v.cambio ? ' × ' + formatarCambioEF(v.cambio) + (v.medio ? ' (médio real)' : '') : '') + ' → R$ ' + moeda(v.brl);
}

// "US$ 1.000,00 + € 200,00" — fornecedor com mais de uma moeda (ou câmbios diferentes). Estrangeiras
// primeiro; a parte em R$ (linha nacional do mesmo fornecedor) por último.
export function textoPartesPorMoeda(porMoeda = []) {
    const ordem = [...porMoeda].sort((a, b) => (a.moeda === 'real') - (b.moeda === 'real'));
    return ordem.map((q) => SIMBOLO_MOEDA_FORN[q.moeda] + ' ' + moeda(Number(q.valor_origem || 0))).join(' + ');
}

// "US$ 1.000,00 + € 200,00 = R$ 6.720,00"
export function textoQuebraPorMoeda(porMoeda = [], totalBrl = null) {
    const total = totalBrl != null ? Number(totalBrl) : porMoeda.reduce((s, q) => s + Number(q.valor_brl || 0), 0);
    return textoPartesPorMoeda(porMoeda) + ' = R$ ' + moeda(total);
}

// Parcela (R$) na moeda do fornecedor: "€ 161,02 × 5,9000" (R$ ÷ câmbio do fornecedor, o mesmo do
// card). BRL puro → null (a parcela já está em R$).
export function textoParcelaNaMoedaFornecedor(valorBrl, v = {}) {
    if (!v.estrangeira || !(Number(v.cambio) > 0)) return null;
    return v.simbolo + ' ' + moeda(Number(valorBrl || 0) / Number(v.cambio)) + ' × ' + formatarCambioEF(v.cambio) + (v.medio ? ' (médio real)' : '');
}

// Origem da PARCELA do Efetivo. Já aprovada (tem financeiroparcela): a origem CONGELADA na aprovação
// (moeda_origem_congelada, a mesma do Contas a Pagar/Aprovados: "€ 930,00 · câmbio 5,95"); sem origem
// congelada → null (só R$). Ainda não aprovada: derivada pelo câmbio atual do fornecedor.
export function textoOrigemParcela(parcela = {}, v = {}) {
    if (parcela.aprovada_financeiro) { // só quando a financeiroparcela existe (API); vínculo órfão = não aprovada
        const o = parcela.moeda_origem_congelada;
        if (o && o.misto) return textoOrigem(o) || null; // fornecedor misto (12/10): quebra congelada por moeda
        if (!o || o.moeda === 'real' || !(Number(o.cambio) > 0) || o.valor_origem === null || o.valor_origem === undefined) return null;
        return o.simbolo + ' ' + moeda(Number(o.valor_origem)) + ' · câmbio ' + Number(o.cambio).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
    }
    // D-0117: parcela com moeda informada pela operadora mostra a moeda dela (R$ = paga em reais, sem origem).
    const m = String(parcela.moeda_origem || '');
    if (m) {
        if (m === 'real' || !SIMBOLO_MOEDA_FORN[m] || !(Number(parcela.cambio_origem) > 0)) return null;
        return SIMBOLO_MOEDA_FORN[m] + ' ' + moeda(Number(parcela.valor_origem || 0)) + ' · câmbio '
            + Number(parcela.cambio_origem).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
    }
    return textoParcelaNaMoedaFornecedor(parcela.valor, v);
}
